import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiErr, API } from "@/api";
import Shell from "@/components/Shell";
import { exhibitorNav } from "./ExhibitorDashboard";
import { STATUS } from "./ExhibitorLeads";
import { Spinner, PriorityBadge } from "@/components/Brand";
import { toast } from "sonner";
import { Sparkles, Plus, X, CalendarPlus, Repeat, Heart } from "lucide-react";
import { useScrollLock } from "@/hooks/useScrollLock";

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [tags, setTags] = useState([]);
  const [interests, setInterests] = useState([]);
  const [status, setStatus] = useState("new");
  const [newTag, setNewTag] = useState("");
  const [newInterest, setNewInterest] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  useScrollLock(taskOpen);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [taskAssignee, setTaskAssignee] = useState("");
  const [staffList, setStaffList] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/exhibitor/leads/${id}`);
    setLead(data); setRemarks(data.remarks || ""); setTags(data.tags || []);
    setInterests(data.interests || []); setStatus(data.followup_status || "new");
  };
  useEffect(() => { load(); api.get("/exhibitor/staff").then(({ data }) => setStaffList(data)).catch(() => {}); /* eslint-disable-next-line */ }, [id]);

  const save = async (patch) => {
    try {
      const { data } = await api.put(`/exhibitor/leads/${id}`, patch);
      setLead(data);
      toast.success("Lead updated");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/exhibitor/leads/${id}/analyze`);
      toast.success(`AI: ${data.priority.toUpperCase()} (${data.score})`);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setAnalyzing(false); }
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    try {
      await api.post("/exhibitor/followups", { lead_id: id, title: taskTitle, due_date: taskDue, assigned_to: taskAssignee });
      toast.success("Follow-up task created");
      setTaskOpen(false); setTaskTitle(""); setTaskDue(""); setTaskAssignee("");
    } catch (err) { toast.error(apiErr(err)); }
  };

  if (!lead) return <Shell nav={exhibitorNav}><Spinner /></Shell>;

  return (
    <Shell nav={exhibitorNav}>

      <div className="glass rounded-3xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-3xl text-slate-100">{lead.name}</h1>
            <p className="text-slate-400 text-sm mt-1 capitalize">{lead.classification} · {lead.source?.replace("_", " ")}</p>
            {lead.phone && <p className="text-slate-300 text-sm mt-1 font-mono-j">{lead.phone}</p>}
            {lead.company && <p className="text-slate-400 text-sm">{lead.company}{lead.designation ? ` · ${lead.designation}` : ""}</p>}
            {lead.email && <p className="text-slate-400 text-sm">{lead.email}</p>}
          </div>
          <div className="text-right space-y-2">
            <PriorityBadge priority={lead.priority} />
            {lead.score != null && <div className="font-display text-2xl gold-text">{lead.score}</div>}
            {lead.visit_count > 1 && <div className="text-[11px] text-sky-300 flex items-center gap-1 justify-end"><Repeat size={11} /> {lead.visit_count} visits</div>}
          </div>
        </div>

        {lead.ai_reason && (
          <div className="mt-4 rounded-xl bg-amber-500/8 border border-amber-500/20 p-3" data-testid="lead-ai-insight">
            <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-wide"><Sparkles size={13} /> AI Insight</div>
            <p className="text-sm text-slate-300 mt-1">{lead.ai_reason}</p>
            {lead.suggested_action && <p className="text-sm text-amber-200/90 mt-1">→ {lead.suggested_action}</p>}
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button onClick={analyze} disabled={analyzing} className="btn-gold px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-60" data-testid="analyze-lead-button">
            <Sparkles size={15} /> {analyzing ? "Analysing…" : "AI Analyse"}
          </button>
          <button onClick={() => setTaskOpen(true)} className="glass px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 text-slate-200" data-testid="add-task-button">
            <CalendarPlus size={15} /> Follow-up
          </button>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 mt-4">
        <label className="text-xs uppercase tracking-wide text-slate-400">Follow-up Status</label>
        <select value={status} onChange={(e) => { setStatus(e.target.value); save({ followup_status: e.target.value }); }} className="w-full mt-2 bg-black/30 gold-border rounded-xl px-3 py-2.5 text-slate-100 capitalize focus:outline-none" data-testid="lead-followup-status-dropdown">
          {STATUS.map((s) => <option key={s} value={s} className="bg-[#12332A] capitalize">{s}</option>)}
        </select>
        <label className="text-xs uppercase tracking-wide text-slate-400 block mt-4">Priority</label>
        <div className="flex gap-2 mt-2" data-testid="lead-priority-picker">
          {["hot", "warm", "cold"].map((pr) => (
            <button key={pr} onClick={() => save({ priority: pr })} className={`flex-1 py-2 rounded-xl text-xs uppercase tracking-wide border ${lead.priority === pr ? "btn-gold border-transparent" : "border-amber-500/20 text-slate-300"}`} data-testid={`lead-priority-${pr}`}>{pr}</button>
          ))}
        </div>
      </div>

      {lead.liked_products?.length > 0 && (
        <div className="glass rounded-2xl p-5 mt-4" data-testid="lead-liked-products">
          <label className="text-xs uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><Heart size={12} className="text-rose-500" fill="currentColor" /> Products they selected ({lead.liked_products.length})</label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
            {lead.liked_products.map((lp) => (
              <div key={lp.product_id} className="rounded-xl overflow-hidden border border-amber-500/15" data-testid={`lead-liked-${lp.product_id}`}>
                <img src={`${API}/files/${lp.image}`} alt={lp.name} className="w-full aspect-square object-cover" />
                <div className="p-1.5">
                  <div className="text-[11px] text-slate-200 truncate">{lp.name}</div>
                  <div className="text-[9px] text-slate-500 truncate">{lp.catalogue_title}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-2">Shared by the visitor from your digital catalogue. This isn't counted as a stall visit.</p>
        </div>
      )}

      <div className="glass rounded-2xl p-5 mt-4">
        <label className="text-xs uppercase tracking-wide text-slate-400">Interests</label>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {interests.map((t) => (
            <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 flex items-center gap-1">{t}
              <X size={11} className="cursor-pointer" onClick={() => { const n = interests.filter((x) => x !== t); setInterests(n); save({ interests: n }); }} /></span>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input value={newInterest} onChange={(e) => setNewInterest(e.target.value)} placeholder="Add interest" className="flex-1 bg-black/30 gold-border rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none" data-testid="interest-input" />
          <button data-testid="interest-add" onClick={() => { if (newInterest.trim()) { const n = [...interests, newInterest.trim()]; setInterests(n); save({ interests: n }); setNewInterest(""); } }} className="btn-gold px-3 rounded-xl"><Plus size={16} /></button>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 mt-4">
        <label className="text-xs uppercase tracking-wide text-slate-400">Tags</label>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tags.map((t) => (
            <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-sky-500/10 text-sky-300 flex items-center gap-1">#{t}
              <X size={11} className="cursor-pointer" onClick={() => { const n = tags.filter((x) => x !== t); setTags(n); save({ tags: n }); }} /></span>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Add tag" className="flex-1 bg-black/30 gold-border rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none" data-testid="tag-input" />
          <button data-testid="tag-add" onClick={() => { if (newTag.trim()) { const n = [...tags, newTag.trim()]; setTags(n); save({ tags: n }); setNewTag(""); } }} className="btn-gold px-3 rounded-xl"><Plus size={16} /></button>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 mt-4">
        <label className="text-xs uppercase tracking-wide text-slate-400">Remarks</label>
        <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} onBlur={() => save({ remarks })} rows={3} placeholder="Notes about this lead…" className="w-full mt-2 bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none" data-testid="lead-remarks" />
      </div>

      {taskOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-4" onClick={() => setTaskOpen(false)}>
          <div className="glass rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="task-modal">
            <h3 className="font-display text-2xl gold-text">New Follow-up</h3>
            <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Task title" className="w-full mt-4 bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="task-title-input" />
            <input type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className="w-full mt-3 bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="task-due-input" />
            <select value={taskAssignee} onChange={(e) => setTaskAssignee(e.target.value)} className="w-full mt-3 bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="task-assignee-input">
              <option value="">Assign to (optional)</option>
              {staffList.map((s) => <option key={s.id} value={s.name}>{s.name} · {s.role}</option>)}
            </select>
            <button onClick={addTask} className="w-full btn-gold py-3 rounded-xl mt-4" data-testid="task-save">Create Task</button>
          </div>
        </div>
      )}
    </Shell>
  );
}
